import {useContext, useEffect, useMemo, useRef, useState} from "react";
import debounce from "lodash/debounce";
import {Alert, Checkbox, ConfigProvider, Form, InputNumber, message, Modal} from "antd";
import {useForm} from "antd/es/form/Form";
import * as globalStyles from "../App.module.css";
import * as styles from "./VulSourceItemMassAiExtractModal.module.css";
import QueryOptions from "../../sal-ui/QueryOptions";
import {AiExtractorServiceContext, AppContextContext, VulSourceItemServiceContext} from "../../Contexts";
import AiExtractor from "../../domain/AiExtractor";
import {Permission} from "../../domain/auth/Permission";
import {buildGroupedAiExtractorOptions, renderAiExtractorOption} from "./AiExtractorSelectOptions";
import {Feature} from "../../domain/Feature";
import SelectAllMultiSelect from "../common/SelectAllMultiSelect";

const AI_EXTRACT_DEFAULT_LIMIT = 100;
const AI_EXTRACT_MAX_LIMIT = 10000;
const PREVIEW_DEBOUNCE_MS = 400;
const DEFAULT_ORDER_BY = "id asc";

type PreviewState =
    | { status: "idle" }
    | { status: "loading" }
    | { status: "success", dispatchCount: number }
    | { status: "failed" };

type PreviewRequest = {
    requestId: number;
    ids: string[];
    maxItems: number;
    overwrite: boolean;
    rqbQuery: any;
    orderBy: string;
    signal: AbortSignal;
};

type Props = {
    open: boolean;
    itemCount: number;
    rqbQuery: any;
    orderBy: string;
    onClose: () => void;
};

function VulSourceItemMassAiExtractModal(props: Props) {
    const appContext = useContext(AppContextContext);
    const vulSourceItemService = useContext(VulSourceItemServiceContext);
    const aiExtractorService = useContext(AiExtractorServiceContext);
    const [form] = useForm();
    const [inProgress, setInProgress] = useState(false);
    const [aiExtractors, setAiExtractors] = useState<AiExtractor[]>();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const limit = Form.useWatch("limit", form) ?? AI_EXTRACT_DEFAULT_LIMIT;
    const overwriteCurrentResult = Form.useWatch("overwriteCurrentResult", form) ?? false;
    const [preview, setPreview] = useState<PreviewState>({status: "idle"});
    const effectiveOrderBy = props.orderBy?.trim() ? props.orderBy : DEFAULT_ORDER_BY;
    const previewAbortControllerRef = useRef<AbortController>();
    const previewRequestIdRef = useRef(0);

    const clearPreview = () => setPreview({status: "idle"});

    const resetPreviewRequestState = () => {
        fetchPreview.cancel();
        previewAbortControllerRef.current?.abort();
        previewRequestIdRef.current += 1;
        clearPreview();
    };

    useEffect(() => {
        if (!props.open) {
            return;
        }

        setSelectedIds([]);
        form.resetFields(["limit", "overwriteCurrentResult"]);
        clearPreview();

        aiExtractorService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setAiExtractors(result.data));
    }, [props.open, aiExtractorService, form]);

    const fetchPreview = useMemo(() => debounce(
        ({requestId, ids, maxItems, overwrite, rqbQuery, orderBy, signal}: PreviewRequest) => {
            const options = new QueryOptions(orderBy, maxItems, 0, undefined, undefined, rqbQuery);

            vulSourceItemService.aiExtractItemsPreview(options, ids, overwrite, signal)
                .then(result => {
                    if (previewRequestIdRef.current === requestId) {
                        setPreview({status: "success", dispatchCount: result.dispatchCount});
                    }
                })
                .catch(error => {
                    if (error?.code === "ERR_CANCELED") {
                        return;
                    }

                    if (previewRequestIdRef.current === requestId) {
                        setPreview({status: "failed"});
                    }
                });
        }, PREVIEW_DEBOUNCE_MS
    ), [vulSourceItemService]);

    useEffect(() => {
        if (!props.open || selectedIds.length === 0) {
            resetPreviewRequestState();

            return;
        }

        fetchPreview.cancel();
        previewAbortControllerRef.current?.abort();

        const requestId = ++previewRequestIdRef.current;
        const controller = new AbortController();

        previewAbortControllerRef.current = controller;

        setPreview({status: "loading"});

        fetchPreview({
            requestId,
            ids: selectedIds,
            maxItems: limit,
            overwrite: overwriteCurrentResult,
            rqbQuery: props.rqbQuery,
            orderBy: effectiveOrderBy,
            signal: controller.signal
        });
    }, [props.open, selectedIds, limit, overwriteCurrentResult, props.rqbQuery, effectiveOrderBy, fetchPreview]);

    useEffect(() => {
        return () => {
            fetchPreview.cancel();
            previewAbortControllerRef.current?.abort();
        };
    }, [fetchPreview]);

    const canRunAiExtraction = appContext.user.hasFeature(Feature.AI)
                               && (appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.AI_EXTRACTOR__MANAGE_ORG));
    const runnableAiExtractors = canRunAiExtraction ? aiExtractors : undefined;
    const aiExtractorOptions = runnableAiExtractors
        ? buildGroupedAiExtractorOptions(runnableAiExtractors, appContext.user.isSystemAdmin())
        : [];

    const effectiveCount = Math.min(props.itemCount, limit);

    const renderPreviewHint = () => {
        if (selectedIds.length === 0) {
            return <i>Select at least one AI extractor to see the count.</i>;
        }

        switch (preview.status) {
            case "loading":
                return <i>Calculating…</i>;
            case "failed":
                return <i>Preview unavailable.</i>;
            case "success": {
                const label = preview.dispatchCount === 1 ? "AI extraction" : "AI extractions";

                return <><b>{preview.dispatchCount}</b> {label} will be performed.</>;
            }
            default:
                return null;
        }
    };

    const getAiExtractorOptionValues = () => aiExtractorOptions
        .flatMap(group => group.options.map(option => option.value))
        .filter(Boolean);

    return (
        <Modal
            open={props.open}
            title={"Mass AI extraction of items"}
            width={900}
            okText={"AI extract"}
            cancelText={"Cancel"}
            okButtonProps={{disabled: inProgress || selectedIds.length === 0}}
            mask={{closable: false}}
            onOk={() => {
                form.validateFields().then((values: { limit: number, overwriteCurrentResult: boolean }) => {
                    const options = new QueryOptions(effectiveOrderBy, values.limit, 0, undefined, undefined, props.rqbQuery);

                    setInProgress(true);

                    vulSourceItemService.aiExtractItems(options, selectedIds, values.overwriteCurrentResult)
                        .then(() => {
                            message.success("AI extraction started.");

                            props.onClose();
                        })
                        .finally(() => setInProgress(false));
                });
            }}
            onCancel={() => props.onClose()}
        >
            <Alert
                style={{marginBottom: 16}}
                type="warning"
                showIcon
                title="This action may consume a large amount of credits."
            />

            <p>
                AI extract up to {effectiveCount} of {props.itemCount} items using selected AI extractors.
                <br/>
                <span className={styles.previewHint}>{renderPreviewHint()}</span>
            </p>

            <ConfigProvider theme={{components: {Form: {itemMarginBottom: 8}}}}>
                <Form form={form} className={globalStyles["common__form"]} labelCol={{span: 8}} wrapperCol={{span: 16}} style={{marginTop: 16}}>
                    <Form.Item label={"AI extractors"} required={true}>
                        <SelectAllMultiSelect
                            style={{width: "100%"}}
                            placeholder="Select AI extractors"
                            loading={!aiExtractors}
                            value={selectedIds}
                            onChange={setSelectedIds}
                            selectAllValues={getAiExtractorOptionValues()}
                            options={aiExtractorOptions}
                            optionFilterProp="label"
                            optionLabelProp="label"
                            optionRender={option => {
                                const aiExtractor = (option.data as { aiExtractor?: AiExtractor }).aiExtractor;

                                return aiExtractor ? renderAiExtractorOption(aiExtractor) : option.label;
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="limit"
                        label={"Maximum number of items"}
                        initialValue={AI_EXTRACT_DEFAULT_LIMIT}
                        rules={[{
                            required: true,
                            type: "integer",
                            min: 1,
                            max: AI_EXTRACT_MAX_LIMIT,
                            message: `Enter an integer between 1 and ${AI_EXTRACT_MAX_LIMIT}.`
                        }]}
                    >
                        <InputNumber min={1} max={AI_EXTRACT_MAX_LIMIT} step={1}/>
                    </Form.Item>

                    <Form.Item
                        name="overwriteCurrentResult"
                        label={"Overwrite existing results"}
                        valuePropName={"checked"}
                        initialValue={false}
                        extra={"When unchecked, AI extraction is skipped if a result for that AI extractor already exists."}
                    >
                        <Checkbox/>
                    </Form.Item>
                </Form>
            </ConfigProvider>
        </Modal>
    );
}

export default VulSourceItemMassAiExtractModal;
